import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { formatMinor, fetchWalletSnapshot, type WalletSnapshot } from '../api/payments';
import {
  closeIdlePlaySessions,
  fetchPlayOptions,
  savePreferences,
  type PlayCurrency,
  type PlayOptions,
} from '../api/preferences';
import { aznMinorToPlayMinor, wageringPercent } from '../lib/fxDisplay';
import { hasManualPaymentRegion, suggestedRegionForCurrency } from '../lib/currencyRegion';
import { fetchWheelStatus } from './lucky-wheel/luckyWheelApi';
import type { WheelWageringStatus } from './lucky-wheel/wheelTypes';
import { BonusPanel } from './BonusPanel';

const FLAGS: Record<string, string> = {
  AZN: '🇦🇿',
  RUB: '🇷🇺',
  EUR: '🇪🇺',
  USD: '🇺🇸',
};

interface Props {
  open: boolean;
  onClose: () => void;
  onUpdated?: (snap: WalletSnapshot) => void;
}

function preferenceErrorMessage(err: unknown, fallback: string, closeGameFirst: string): string {
  if (err instanceof ApiError) {
    const body = err.body;
    let code = '';
    if (typeof body === 'string') code = body;
    else if (body && typeof body === 'object') {
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === 'string') code = detail;
      else if (detail && typeof detail === 'object' && detail !== null && 'code' in detail) {
        code = String((detail as { code: unknown }).code || '');
      }
    }
    if (code.includes('close_active_game_first') || err.status === 409) {
      return closeGameFirst;
    }
    if (code) return code;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function BalanceMenu({ open, onClose, onUpdated }: Props) {
  const { t, i18n } = useTranslation(['payments', 'wallet', 'common', 'errors']);
  const navigate = useNavigate();
  const [options, setOptions] = useState<PlayOptions | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [wagering, setWagering] = useState<WheelWageringStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setBonusOpen(false);
      setCurrencyOpen(false);
      return;
    }
    let cancelled = false;
    setError(null);
    setCurrencyOpen(false);
    void Promise.all([fetchPlayOptions(), fetchWalletSnapshot(), fetchWheelStatus().catch(() => null)])
      .then(([opts, snap, status]) => {
        if (cancelled) return;
        setOptions(opts);
        setWallet(snap);
        setWagering(status?.wagering || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'error');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Portal under document.body — site-header's backdrop-filter traps position:fixed children.
  // Soft scroll lock: overflow only (no body position:fixed) so the page does not jump.
  useEffect(() => {
    if (!open) return;
    const { body, documentElement } = document;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = documentElement.style.overflow;
    const prevOverscroll = documentElement.style.overscrollBehavior;
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';

    const syncTop = () => {
      const header = document.querySelector('.site-header') as HTMLElement | null;
      const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 56;
      documentElement.style.setProperty('--balance-menu-top', `${Math.max(bottom, 48) + 6}px`);
    };
    syncTop();
    window.addEventListener('resize', syncTop);
    window.visualViewport?.addEventListener('resize', syncTop);
    window.visualViewport?.addEventListener('scroll', syncTop);
    return () => {
      body.style.overflow = prevBodyOverflow;
      documentElement.style.overflow = prevHtmlOverflow;
      documentElement.style.overscrollBehavior = prevOverscroll;
      documentElement.style.removeProperty('--balance-menu-top');
      window.removeEventListener('resize', syncTop);
      window.visualViewport?.removeEventListener('resize', syncTop);
      window.visualViewport?.removeEventListener('scroll', syncTop);
    };
  }, [open]);

  const play = (wallet?.play_currency || options?.preferences?.play_currency || 'AZN') as PlayCurrency;
  const realPlay = wallet?.play_balance_real_minor ?? wallet?.balance_real_minor ?? 0;
  const bonusPlay = wallet?.play_balance_bonus_minor ?? wallet?.balance_bonus_minor ?? 0;
  const currencies = (options?.play_currencies || []).filter((row) =>
    ['AZN', 'RUB', 'EUR', 'USD'].includes(row.code),
  );
  const rate = options?.fx?.rates_azn_per_unit?.[play] || (play === 'AZN' ? '1' : '1');
  const locale = i18n.language || 'az';

  const wagerUi = useMemo(() => {
    const targetAzn = Number(wagering?.wagering_target_minor || 0) || 0;
    const doneAzn = Number(wagering?.wagering_completed_minor || 0) || 0;
    if (targetAzn <= 0) return null;
    const toPlay = (azn: number) => aznMinorToPlayMinor(azn, play === 'AZN' ? 1 : rate);
    return {
      pct: wageringPercent(doneAzn, targetAzn),
      from: formatMinor(toPlay(doneAzn), play, locale),
      to: formatMinor(toPlay(targetAzn), play, locale),
    };
  }, [wagering, play, rate, locale]);

  if (!open) return null;

  async function selectCurrency(code: PlayCurrency) {
    if (busy || code === play) {
      setCurrencyOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await closeIdlePlaySessions().catch(() => undefined);
      const prefs: { play_currency: PlayCurrency; payment_region?: string } = { play_currency: code };
      // Auto-align payment region with currency unless the user picked a region manually.
      if (!hasManualPaymentRegion()) {
        const region = suggestedRegionForCurrency(code);
        if (region) prefs.payment_region = region;
      }
      await savePreferences(prefs);
      try {
        localStorage.setItem('vbbplay.play_currency_manual', '1');
      } catch {
        // ignore
      }
      const [opts, snap] = await Promise.all([fetchPlayOptions(), fetchWalletSnapshot()]);
      setOptions(opts);
      setWallet(snap);
      onUpdated?.(snap);
      setCurrencyOpen(false);
    } catch (err) {
      setError(
        preferenceErrorMessage(
          err,
          t('errors:generic', { defaultValue: 'Xəta' }),
          t('payments:closeGameFirst'),
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <>
      <div className="balance-menu-backdrop balance-menu-backdrop-ton" role="presentation" onClick={onClose}>
        <div
          className="balance-menu-sheet balance-menu-sheet-ton"
          role="dialog"
          aria-label={t('payments:balanceTitle')}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="balance-menu-head">
            <strong>{t('payments:balanceTitle')}</strong>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common:close')}>
              ×
            </button>
          </header>

          <div className="balance-menu-grid-ton">
            <div className="balance-menu-card-ton">
              <span className="muted">{t('payments:realBalance')}</span>
              <strong>{formatMinor(realPlay, play, locale)}</strong>
            </div>
            <div className="balance-menu-card-ton balance-menu-currency-ton">
              <span className="muted">{t('payments:gameCurrency')}</span>
              <button
                type="button"
                className="balance-currency-btn"
                disabled={busy}
                aria-expanded={currencyOpen}
                onClick={() => setCurrencyOpen((v) => !v)}
              >
                <span>
                  {FLAGS[play] || ''} {play}
                </span>
                <span aria-hidden="true">⌄</span>
              </button>
              {currencyOpen ? (
                <ul className="balance-currency-list" role="listbox">
                  {currencies.map((row) => (
                    <li key={row.code}>
                      <button type="button" disabled={busy} onClick={() => void selectCurrency(row.code)}>
                        <span>
                          {FLAGS[row.code] || ''} {row.code}
                        </span>
                        {row.code === play ? <span>✓</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <button
            type="button"
            className="balance-menu-bonus-ton"
            onClick={() => {
              onClose();
              navigate('/bonus-games');
            }}
          >
            <div className="balance-menu-bonus-main">
              <div className="balance-menu-bonus-top">
                <span>
                  <span className="muted">{t('payments:bonusAccount')}</span>
                  <strong>{formatMinor(bonusPlay, play, locale)}</strong>
                </span>
                <span className="balance-menu-chevron" aria-hidden="true">
                  ›
                </span>
              </div>
              {wagerUi ? (
                <div className="balance-bonus-progress" aria-label={`${wagerUi.pct}%`}>
                  <div className="balance-bonus-progress-track">
                    <div className="balance-bonus-progress-fill" style={{ width: `${wagerUi.pct}%` }} />
                  </div>
                  <div className="balance-bonus-progress-bounds">
                    <span>
                      {t('payments:bonusFrom', { defaultValue: 'from {{amount}}', amount: wagerUi.from })}
                    </span>
                    <span>
                      {t('payments:bonusTo', { defaultValue: 'to {{amount}}', amount: wagerUi.to })}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </button>

          {error ? <p className="form-error">{error}</p> : null}

          <Link to="/cashier?tab=deposit" className="btn btn-primary balance-menu-cta" onClick={onClose}>
            {t('payments:walletCta')}
          </Link>
        </div>
      </div>

      <BonusPanel
        open={bonusOpen}
        onClose={() => setBonusOpen(false)}
        playCurrency={play}
        rateAznPerUnit={rate}
        locale={locale}
      />
    </>,
    document.body,
  );
}
